import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Book,
  ArrowRight,
  Home,
  Users,
  Mail,
  QrCode,
  Settings,
  Star,
  CreditCard,
  Code,
  ExternalLink,
  Clock,
} from "lucide-react";

function Navigation() {
  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link href="/" data-testid="link-home">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-features">
            Features
          </Link>
          <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-pricing">
            Pricing
          </Link>
          <Link href="/docs" className="text-sm text-foreground font-medium" data-testid="link-docs">
            Docs
          </Link>
          <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-blog">
            Blog
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link href="/login">
            <Button variant="ghost" size="sm" data-testid="button-login">
              Login
            </Button>
          </Link>
          <Link href="/signup">
            <Button size="sm" data-testid="button-signup">
              Sign Up Free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
          <nav className="flex flex-wrap items-center justify-center gap-6">
            <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-features">
              Features
            </Link>
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-pricing">
              Pricing
            </Link>
            <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-docs">
              Docs
            </Link>
            <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-blog">
              Blog
            </Link>
            <Link href="/contact" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-contact">
              Contact
            </Link>
            <Link href="/privacy" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-privacy">
              Privacy
            </Link>
            <Link href="/terms" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-terms">
              Terms
            </Link>
            <Link href="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-login">
              Login
            </Link>
          </nav>
          <div className="text-sm text-muted-foreground">
            {new Date().getFullYear()} ReviewKit. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}

const sections = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Home,
    content: [
      {
        subtitle: "Overview",
        text: "ReviewKit is a reputation management platform designed for local service businesses (HVAC, plumbing, electricians, etc.) to collect Google Reviews through email campaigns and QR codes.",
      },
      {
        subtitle: "Quick Start",
        text: "1. Sign up for a free account\n2. Add your Google Review URL in Settings\n3. Add your staff members\n4. Start sending review requests or generate QR codes",
      },
    ],
  },
  {
    id: "dashboard",
    title: "Dashboard",
    icon: Home,
    content: [
      {
        subtitle: "Overview Stats",
        text: "Your dashboard shows key metrics: Requests Sent (total emails sent), Response Rate (% of customers who responded), and Average Rating (star rating received).",
      },
      {
        subtitle: "Monthly Usage",
        text: "Track your review request usage for the current month. The usage indicator shows how many requests you've sent and your plan limit. You'll see warnings when approaching your limit and can upgrade directly from the dashboard.",
      },
      {
        subtitle: "Recent Activity",
        text: "View recent customer interactions including the associated staff member, source (email or QR), rating given, and status.",
      },
    ],
  },
  {
    id: "history",
    title: "Request History",
    icon: Clock,
    content: [
      {
        subtitle: "Viewing Past Requests",
        text: "The History page shows all review requests you've sent. Each entry displays the customer name, email, staff member, date sent, and current status.",
      },
      {
        subtitle: "Request Statuses",
        text: "• Sent: Email delivered but no action yet\n• Clicked: Customer opened the review page\n• Reviewed: Customer submitted a rating",
      },
      {
        subtitle: "Search & Pagination",
        text: "Use the search bar to find requests by customer name or email. Results are paginated for easy browsing through large request histories.",
      },
    ],
  },
  {
    id: "staff-management",
    title: "Staff Management",
    icon: Users,
    content: [
      {
        subtitle: "Adding Staff",
        text: "Navigate to Staff page and click 'Add Staff'. Enter the staff member's name. Each staff member gets their own unique QR code for tracking performance.",
      },
      {
        subtitle: "Staff Limits",
        text: "Free plan: 1 staff member. Lifetime and Pro plans: Unlimited staff members.",
      },
      {
        subtitle: "Managing Staff",
        text: "Toggle staff active/inactive status, edit their information, or remove them entirely. Inactive staff won't appear in review request dropdowns.",
      },
    ],
  },
  {
    id: "sending-requests",
    title: "Sending Review Requests",
    icon: Mail,
    content: [
      {
        subtitle: "Request Form",
        text: "Fill in the customer's name, email, select the staff member who completed the job, and optionally add a job description for context.",
      },
      {
        subtitle: "Email Preview",
        text: "See a live preview of how your branded email will appear to customers before sending.",
      },
      {
        subtitle: "Request Limits",
        text: "Free: 10 requests/month. Lifetime: 500 requests/month. Pro: Unlimited requests. Your usage resets at the beginning of each calendar month.",
      },
      {
        subtitle: "Duplicate Protection",
        text: "ReviewKit prevents sending multiple requests to the same email address within 24 hours, protecting both you and your customers from accidental spam.",
      },
    ],
  },
  {
    id: "qr-codes",
    title: "QR Codes",
    icon: QrCode,
    content: [
      {
        subtitle: "Generating Codes",
        text: "Access the QR Codes page to view and download codes for your business and each staff member. Each code links to your public review page.",
      },
      {
        subtitle: "Use Cases",
        text: "Print QR codes for: vehicle signage, business cards, service completion receipts, in-store displays, and leave-behind materials.",
      },
    ],
  },
  {
    id: "settings",
    title: "Settings & Configuration",
    icon: Settings,
    content: [
      {
        subtitle: "Business Information",
        text: "Update your business name, customize your review page URL slug, and set your owner email address.",
      },
      {
        subtitle: "Google Review Integration",
        text: "Add your Google Review URL. This link is offered to all customers on the thank-you page after they complete their feedback.",
      },
      {
        subtitle: "Brand Color",
        text: "Choose your brand color to customize the appearance of your public review page and email templates.",
      },
      {
        subtitle: "Business Logo",
        text: "Upload your company logo (PNG, JPG, GIF, WebP, or SVG, max 2MB). Enable 'Show logo in emails' to include it at the top of review request emails sent to your customers.",
      },
      {
        subtitle: "Email Template",
        text: "Customize your email template using variables: {{customer_name}}, {{staff_name}}, {{business_name}}, {{job_description}}.",
      },
    ],
  },
  {
    id: "public-review-page",
    title: "Public Review Page",
    icon: Star,
    content: [
      {
        subtitle: "Customer Experience",
        text: "When customers visit your review page (via QR code or email link), they see a mobile-optimized page with your branding and a 5-star rating interface.",
      },
      {
        subtitle: "Rating & Feedback",
        text: "After selecting any star rating (1-5), customers can optionally share additional feedback about their experience. This feedback is stored privately for your review.",
      },
      {
        subtitle: "Google Review Option",
        text: "All customers are shown a thank-you page with a button to leave a Google Review. This ensures equal treatment for all customers regardless of their rating, in compliance with Google's review policies.",
      },
    ],
  },
  {
    id: "billing",
    title: "Billing & Plans",
    icon: CreditCard,
    content: [
      {
        subtitle: "Free Plan",
        text: "$0 forever. Includes 10 requests/month, 1 staff member, QR codes, and basic dashboard.",
      },
      {
        subtitle: "Lifetime Plan",
        text: "$79 one-time payment. Includes 500 requests/month, unlimited staff, priority support, and all future updates.",
      },
      {
        subtitle: "Pro Plan",
        text: "$29/month. Includes unlimited requests, everything in Lifetime, priority email support, and first access to new features.",
      },
      {
        subtitle: "Managing Subscription",
        text: "Access your billing portal from Settings to update payment methods, view invoices, or cancel your subscription.",
      },
    ],
  },
  {
    id: "api-reference",
    title: "API Reference",
    icon: Code,
    content: [
      {
        subtitle: "Business Endpoints",
        text: "GET /api/business - Get current business\nPATCH /api/business - Update business settings\nGET /api/business/stats - Get dashboard statistics",
      },
      {
        subtitle: "Staff Endpoints",
        text: "GET /api/staff - List all staff\nPOST /api/staff - Add staff member\nPATCH /api/staff/:id - Update staff member\nDELETE /api/staff/:id - Remove staff member",
      },
      {
        subtitle: "Review Request Endpoints",
        text: "GET /api/review-requests - List review requests\nPOST /api/review-requests - Send review request",
      },
      {
        subtitle: "Public Endpoints",
        text: "GET /api/public/review/:businessSlug/:staffSlug? - Get public review page data\nPOST /api/public/feedback - Submit customer feedback",
      },
    ],
  },
];

function TableOfContents() {
  return (
    <div className="hidden lg:block sticky top-24 w-64 shrink-0">
      <div className="text-sm font-semibold mb-4">On This Page</div>
      <nav className="space-y-1">
        {sections.map((section) => (
          <a
            key={section.id}
            href={`#${section.id}`}
            className="flex items-center gap-2 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            data-testid={`toc-link-${section.id}`}
          >
            <section.icon className="h-4 w-4" />
            {section.title}
          </a>
        ))}
      </nav>
    </div>
  );
}

function DocSection({ section }: { section: typeof sections[0] }) {
  return (
    <section id={section.id} className="scroll-mt-24 mb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
          <section.icon className="h-5 w-5 text-primary" />
        </div>
        <h2 className="text-2xl font-semibold" data-testid={`heading-${section.id}`}>
          {section.title}
        </h2>
      </div>
      <div className="space-y-6 pl-1">
        {section.content.map((item, index) => (
          <div key={index}>
            <h3 className="text-lg font-medium mb-2">{item.subtitle}</h3>
            <p className="text-muted-foreground whitespace-pre-line">{item.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Docs() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-md bg-primary/10 flex items-center justify-center">
              <Book className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold" data-testid="heading-docs-title">
                Documentation
              </h1>
              <p className="text-muted-foreground mt-1">
                Everything you need to know about using ReviewKit
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link href="/signup">
              <Button className="gap-2" data-testid="button-get-started">
                Get Started
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link href="/pricing">
              <Button variant="outline" className="gap-2" data-testid="button-view-plans">
                View Plans
                <ExternalLink className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>

        <div className="flex gap-12">
          <div className="flex-1 min-w-0">
            {sections.map((section) => (
              <DocSection key={section.id} section={section} />
            ))}
          </div>
          <TableOfContents />
        </div>
      </main>
      <Footer />
    </div>
  );
}
